import React, { SetStateAction, useCallback, useMemo } from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import { withLocalePrefix } from 'shared/utils/translation';
import {
  fetchSavedSearch,
  createSavedSearch,
  updateSavedSearch,
} from 'talent_matching/api';
import { useUserContext } from 'talent_matching/contexts/user_context';
import SavedFilterButtons from 'talent_matching/components/filters/saved_filter_buttons';
import LoadFiltersModal from 'talent_matching/talent_rediscovery/components/filters/load_filters_modal';
import SaveFiltersModal from 'talent_search/talent_rediscovery/modals/save_filters_modal';
import type { GreenhouseFilterPreset } from 'talent_matching/talent_rediscovery/types';

const t = withLocalePrefix('talent_rediscovery.filters.labels');

export type SavedFiltersProps = {
  featureType: string;
  savedFilterSet: Item | null;
  setSavedFilterSet: (item: Item | null) => void;
  setSavedSearchOptions: (options: SetStateAction<Item[]>) => void;
  formDefaults: Record<string, unknown>;
  savedSearchOptions: Item[];
  parseSavedSearch: (response: unknown) => Record<string, unknown>;
  buildQueryString: () => string;
  greenhouseFilterPresets?: GreenhouseFilterPreset[];
};

const SavedFilters = ({
  featureType,
  savedFilterSet,
  setSavedFilterSet,
  setSavedSearchOptions,
  formDefaults,
  savedSearchOptions,
  parseSavedSearch,
  buildQueryString,
  greenhouseFilterPresets,
}: SavedFiltersProps) => {
  const { reset, setValue } = useFormContext();
  const {
    paths: { savedSearchesPath },
  } = useUserContext();

  const filterNames = useMemo(
    () => savedSearchOptions.map((o) => o.label),
    [savedSearchOptions]
  );

  const dropdownOptions: Item[] = useMemo(() => {
    if (!greenhouseFilterPresets?.length) {
      return savedSearchOptions;
    }

    const greenhouseItems: Item[] = greenhouseFilterPresets.map((preset) => ({
      label: preset.label,
      value: preset.value,
    }));

    return [
      {
        label: t('greenhouse_suggested_filters'),
        value: 'greenhouse_filters',
        disabled: true,
        items: greenhouseItems,
      },
      {
        label: t('your_filters'),
        value: 'your_filters',
        disabled: true,
        items: savedSearchOptions,
      },
    ];
  }, [greenhouseFilterPresets, savedSearchOptions]);

  const [loadModalOpen, openLoadModal, closeLoadModal] = useModal();
  const [saveModalOpen, openSaveModal, closeSaveModal] = useModal();

  const handleLoadSelect = useCallback(
    async (option: Item | null) => {
      if (!option) {
        setSavedFilterSet(null);
        reset(formDefaults);
        return;
      }

      const preset = greenhouseFilterPresets?.find(
        (p) => p.value === option.value
      );

      if (preset) {
        const displayItem = preset.shortLabel
          ? { ...option, label: preset.shortLabel }
          : option;
        setSavedFilterSet(displayItem);
        reset(formDefaults);
        Object.keys(preset.filterValues).forEach((key) => {
          setValue(key, preset.filterValues[key], { shouldDirty: true });
        });
        return;
      }

      setSavedFilterSet(option);

      const response = await fetchSavedSearch(
        savedSearchesPath,
        option.value as unknown as number,
        featureType
      );
      const formFields = parseSavedSearch(response);
      reset(formDefaults);
      Object.keys(formFields).forEach((key) => {
        setValue(key, formFields[key], { shouldDirty: true });
      });
    },
    [
      setSavedFilterSet,
      formDefaults,
      greenhouseFilterPresets,
      savedSearchesPath,
      featureType,
      parseSavedSearch,
      reset,
      setValue,
    ]
  );

  const handleSaveFilters = useCallback(
    async (name: string) => {
      const response = await createSavedSearch(
        savedSearchesPath,
        buildQueryString(),
        name,
        featureType
      );
      const newOption = {
        value: response.id.toString(),
        label: response.label,
      };
      setSavedSearchOptions((prev: Item[]) => [newOption, ...prev]);
      setSavedFilterSet(newOption);
      closeSaveModal();
    },
    [
      savedSearchesPath,
      buildQueryString,
      featureType,
      setSavedSearchOptions,
      setSavedFilterSet,
      closeSaveModal,
    ]
  );

  const handleUpdateFilters = useCallback(async () => {
    if (!savedFilterSet?.value) {
      return;
    }
    await updateSavedSearch(
      savedSearchesPath,
      savedFilterSet.value as unknown as number,
      buildQueryString(),
      savedFilterSet.label,
      featureType
    );
  }, [savedSearchesPath, savedFilterSet, buildQueryString, featureType]);

  const isGreenhousePreset = greenhouseFilterPresets?.some(
    (p) => p.value === savedFilterSet?.value
  );

  return (
    <>
      <SavedFilterButtons
        savedFilterSet={savedFilterSet}
        onLoadFilters={openLoadModal}
        onSaveFilters={openSaveModal}
        onUpdateFilters={isGreenhousePreset ? undefined : handleUpdateFilters}
      />
      <LoadFiltersModal
        isOpen={loadModalOpen}
        onDismiss={closeLoadModal}
        options={dropdownOptions}
        onSelect={handleLoadSelect}
      />
      <SaveFiltersModal
        isOpen={saveModalOpen}
        close={closeSaveModal}
        onSave={handleSaveFilters}
        filterNames={filterNames}
      />
    </>
  );
};

export default SavedFilters;
